import { useEffect, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "./lib/supabase";
import type { AppProfile } from "./types";
import Login from "./components/Login";
import DashboardStats from "./components/DashboardStats";
import SearchHistorical from "./components/SearchHistorical";
import EstimateWorkbench from "./components/EstimateWorkbench";
import ArticleLibrary from "./components/ArticleLibrary";
import Catalogue from "./components/Catalogue";
import CatalogueCandidates from "./components/CatalogueCandidates";
import Sources from "./components/Sources";
import AdminUsers from "./components/AdminUsers";
import ParametricPriceEngine, { type ParametricEngineDraft } from "./components/ParametricPriceEngine";
import type { EngineEstimateTarget } from "./components/SaveParametricDraft";
import PricingResources from "./components/PricingResources";
import HistoricalNoteCandidates from "./components/HistoricalNoteCandidates";
import HistoricalYieldReview from "./components/HistoricalYieldReview";

type Tab = "library" | "estimates" | "engine" | "resources" | "historical" | "yields" | "search" | "candidates" | "catalogue" | "sources" | "admin";

function LoadingScreen() {
  return (
    <main className="center-screen">
      <div className="loading-card card">
        <div className="brand-mark small">L37</div>
        <strong>A carregar L37 Preços…</strong>
      </div>
    </main>
  );
}

function PendingAccess({ profile }: { profile: AppProfile | null }) {
  return (
    <main className="center-screen">
      <section className="pending-card card">
        <div className="brand-mark small">L37</div>
        <p className="eyebrow">Acesso pendente</p>
        <h1>Conta ainda não ativada</h1>
        <p className="muted">
          A autenticação foi aceite, mas esta conta ainda não tem acesso ativo à
          Base de Preços L37.
        </p>
        {profile?.email ? <p className="mono">{profile.email}</p> : null}
        <button
          className="button button-secondary"
          type="button"
          onClick={() => void supabase.auth.signOut()}
        >
          Sair
        </button>
      </section>
    </main>
  );
}

function AppShell({ profile }: { profile: AppProfile }) {
  const [libraryDirty,setLibraryDirty]=useState(false);
  function mayLeaveLibrary(){return tab!=="library" || !libraryDirty || window.confirm("Tem alterações ainda não aplicadas na ficha. Quer sair e descartá-las?");}
  const [tab, setTab] = useState<Tab>("library");
  const [engineDraft, setEngineDraft] = useState<ParametricEngineDraft | null>(null);

  const [engineTarget, setEngineTarget] = useState<EngineEstimateTarget | null>(null);

  const [libraryTarget, setLibraryTarget] = useState<EngineEstimateTarget | null>(null);

  const nav = [
    ["library", "Biblioteca"],
    ["estimates", "Orçamentação"],
    ["engine", "Gerador"],
    ["resources", "Recursos"],
    ["historical", "Notas históricas"],
    ["yields", "Rendimentos"],
    ["search", "Pesquisa"],
    ["candidates", "Candidatos"],
    ["catalogue", "Catálogo"],
    ["sources", "Fontes"],
  ] as const;

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="sidebar-brand">
          <div className="brand-mark small">L37</div>
          <div>
            <strong>Preços</strong>
            <small>Base interna</small>
          </div>
        </div>

        <nav>
          {nav.filter(([value]) => value === "library" || value === "estimates").map(([value, label]) => (
            <button
              className={tab === value ? "nav-item active" : "nav-item"}
              key={value}
              type="button"
              onClick={() => { if(!mayLeaveLibrary())return; if (value === "library") setLibraryTarget(null); setTab(value); }}
            >
              {label}
            </button>
          ))}

          <details className="archive-nav"><summary>Ferramentas e arquivo</summary>
            {nav.filter(([value]) => value !== "library" && value !== "estimates").map(([value, label]) => <button className={tab === value ? "nav-item active" : "nav-item"} key={value} type="button" onClick={() => {if(mayLeaveLibrary())setTab(value);}}>{label}</button>)}
          </details>

          {profile.role === "admin" ? (
            <button
              className={tab === "admin" ? "nav-item active" : "nav-item"}
              type="button"
              onClick={() => {if(mayLeaveLibrary())setTab("admin");}}
            >
              Administração
            </button>
          ) : null}
        </nav>

        <div className="sidebar-footer">
          <div>
            <strong>{profile.full_name || profile.email || "Utilizador"}</strong>
            <small>{profile.role}</small>
          </div>
          <button
            className="text-button"
            type="button"
            onClick={() => {if(mayLeaveLibrary())void supabase.auth.signOut();}}
          >
            Sair
          </button>
        </div>
      </aside>

      <main className="main-content">
        <div className="content-wrap">
          {tab === "library" ? <ArticleLibrary onDirtyChange={setLibraryDirty} canEdit={profile.role === "admin" || profile.role === "estimator"} target={libraryTarget} onReturn={() => setTab("estimates")} onConfigure={(base, workCode) => { setEngineTarget(libraryTarget); setEngineDraft({ workCode, values: base.parameter_values, historicalYield: null, articleBase: base }); setTab("engine"); }} /> : null}
          {tab === "estimates" ? <EstimateWorkbench profile={profile} preferredEstimateId={libraryTarget?.estimateId ?? engineTarget?.estimateId} onOpenLibrary={(target) => { setLibraryTarget(target); setTab("library"); }} onOpenEngine={(target) => { setEngineTarget(target); setEngineDraft(null); setTab("engine"); }} /> : null}
          {tab === "engine" ? <ParametricPriceEngine target={engineTarget} onReturn={() => setTab("estimates")} draft={engineDraft} onDraftChange={setEngineDraft} canUseHistoricalYield={profile.role === "admin" || profile.role === "estimator"} onReviewYields={() => setTab("yields")} /> : null}
          {tab === "resources" ? <PricingResources profile={profile} /> : null}
          {tab === "historical" ? <HistoricalNoteCandidates profile={profile} /> : null}
          {tab === "yields" ? <HistoricalYieldReview profile={profile} /> : null}
          {tab === "search" ? (
            <>
              <DashboardStats />
              <SearchHistorical />
            </>
          ) : null}
          {tab === "candidates" ? <CatalogueCandidates profile={profile} /> : null}
          {tab === "catalogue" ? <Catalogue /> : null}
          {tab === "sources" ? <Sources /> : null}
          {tab === "admin" && profile.role === "admin" ? <AdminUsers /> : null}
        </div>
      </main>
    </div>
  );
}

export default function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<AppProfile | null>(null);
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [loadingProfile, setLoadingProfile] = useState(false);

  useEffect(() => {
    let mounted = true;

    void supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session);
      setLoadingAuth(false);
    });

    const { data: listener } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      if (!mounted) return;

      setSession(nextSession);
      setLoadingAuth(false);

      // Supabase can emit SIGNED_IN/TOKEN_REFRESHED repeatedly for the same
      // authenticated user (for example on focus/session refresh). Do not
      // discard an already-loaded application profile in those cases,
      // otherwise the UI falls back to the access/login gates even though
      // the Supabase session is still valid.
      setProfile((currentProfile) => {
        if (!nextSession) return null;
        if (currentProfile?.user_id === nextSession.user.id) return currentProfile;
        return null;
      });

      if (!nextSession) {
        setLoadingProfile(false);
      }
    });

    return () => {
      mounted = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!session?.user.id) {
      setProfile(null);
      setLoadingProfile(false);
      return;
    }

    let cancelled = false;
    setLoadingProfile(true);

    void supabase
      .from("app_profiles")
      .select("user_id,email,full_name,role,is_active")
      .eq("user_id", session.user.id)
      .maybeSingle()
      .then(({ data, error }) => {
        if (cancelled) return;

        if (error) {
          console.error("Unable to load app profile", error);
          setProfile(null);
        } else {
          setProfile((data as AppProfile | null) ?? null);
        }
        setLoadingProfile(false);
      });

    return () => {
      cancelled = true;
    };
  }, [session?.user.id]);

  if (loadingAuth || loadingProfile) return <LoadingScreen />;
  if (!session) return <Login />;
  if (!profile?.is_active) return <PendingAccess profile={profile} />;

  return <AppShell profile={profile} />;
}
